// ===============================================================
// 🔒 RequireGender.js — منع الدخول بدون تحديد الجنس + تحقق الفتاة
// ===============================================================
//
// Redirect targets and their triggering conditions are UNCHANGED from
// the original implementation - only the underlying account-document
// read moved to AccountSessionStateContext (mounted once, above the
// route tree), so navigating between guarded routes no longer re-fetches
// the same users/{uid} document on every remount. See
// AccountSessionStateContext.js for the exact, single Firestore read and
// the same accountStatus/gender/verification/developer-claim checks this
// file has always relied on.

import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  useAccountSessionState,
  ACCOUNT_ACCESS_ALLOWED,
  ACCOUNT_REASON_PENDING_DELETE,
  ACCOUNT_REASON_SUSPENDED,
  ACCOUNT_REASON_NEEDS_GENDER,
  ACCOUNT_REASON_NEEDS_VERIFICATION,
  ACCOUNT_REASON_SIGNED_OUT,
  ACCOUNT_REASON_ERROR,
} from "../context/AccountSessionStateContext";
import AccountErrorRecovery from "./AccountErrorRecovery";

const RequireGender = ({ children }) => {
  const navigate = useNavigate();
  const { isReady } = useContext(AuthContext);
  const { loading, access, reason, refresh } = useAccountSessionState();

  useEffect(() => {
    if (!isReady || loading || access === ACCOUNT_ACCESS_ALLOWED) {
      return;
    }

    switch (reason) {
      case ACCOUNT_REASON_SIGNED_OUT:
        navigate("/");
        return;
      case ACCOUNT_REASON_NEEDS_GENDER:
        navigate("/select-gender");
        return;
      case ACCOUNT_REASON_PENDING_DELETE:
        navigate("/pending-delete");
        return;
      case ACCOUNT_REASON_SUSPENDED:
        navigate("/account-suspended", { replace: true });
        return;
      case ACCOUNT_REASON_NEEDS_VERIFICATION:
        navigate("/verification");
        return;
      default:
        // ACCOUNT_REASON_ERROR: no prior redirect target exists for a
        // thrown read - stay put (render AccountErrorRecovery below,
        // not a blank page) rather than invent a new destination.
        return;
    }
  }, [isReady, loading, access, reason, navigate]);

  if (!isReady || loading) {
    return null;
  }

  if (access !== ACCOUNT_ACCESS_ALLOWED) {
    // ACCOUNT_REASON_ERROR is the one blocked reason with no redirect
    // target above - every other reason is already mid-navigation to a
    // real destination of its own (and null is the right render for
    // that single frame). This is the one case that would otherwise
    // leave the user on a permanently blank page after a thrown
    // refresh - give them a visible, actionable way out instead.
    if (reason === ACCOUNT_REASON_ERROR) {
      return <AccountErrorRecovery onRetry={refresh} />;
    }
    return null;
  }

  return children;
};

export default RequireGender;
